import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bell, Check, Briefcase, MessageSquare, DollarSign, CheckCircle, XCircle, Rocket, PartyPopper, Unlock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const notificationIcons = {
  new_job: Briefcase,
  job_application: Briefcase,
  new_message: MessageSquare,
  quote_sent: DollarSign,
  quote_accepted: CheckCircle,
  quote_rejected: XCircle,
  job_started: Rocket,
  job_completed: PartyPopper,
  lead_purchased: Unlock,
};

const notificationColors = {
  new_job: "bg-blue-100 text-blue-600",
  job_application: "bg-purple-100 text-purple-600",
  new_message: "bg-green-100 text-green-600",
  quote_sent: "bg-yellow-100 text-yellow-600",
  quote_accepted: "bg-green-100 text-green-600",
  quote_rejected: "bg-red-100 text-red-600",
  job_started: "bg-blue-100 text-blue-600",
  job_completed: "bg-green-100 text-green-600",
  lead_purchased: "bg-purple-100 text-purple-600",
};

export const NotificationBell = ({ userType, userId, userEmail }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    if (!userId && !userEmail) return;
    
    setLoading(true);
    try {
      let response;
      if (userType === "contractor" && userId) {
        response = await axios.get(`${API}/notifications/${userType}/${userId}`);
      } else if (userEmail) {
        response = await axios.get(`${API}/notifications/email/${encodeURIComponent(userEmail)}`);
      }
      
      if (response?.data) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userEmail, userType]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    try {
      await axios.patch(`${API}/notifications/mark-all-read?user_type=${userType}&user_id=${userId}`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-[#f1f3f4] transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-[#5f6368]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-[#dadce0] z-50 overflow-hidden">
          <div className="p-4 border-b border-[#dadce0] flex items-center justify-between">
            <h3 className="font-semibold text-[#202124]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-[#1a73e8] hover:underline flex items-center gap-1"
              >
                <Check className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-6 h-6 border-2 border-[#1a73e8] border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-[#5f6368]">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notification) => {
                const Icon = notificationIcons[notification.type] || Bell;
                const colorClass = notificationColors[notification.type] || "bg-gray-100 text-gray-600";
                
                return (
                  <Link
                    key={notification.id}
                    to={notification.link || "#"}
                    onClick={() => handleNotificationClick(notification)}
                    className={`block p-4 border-b border-[#f1f3f4] hover:bg-[#f8f9fa] transition-colors ${
                      !notification.read ? "bg-[#e8f0fe]" : ""
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? "font-semibold" : ""} text-[#202124] line-clamp-1`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-[#5f6368] line-clamp-2 mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-xs text-[#80868b] mt-1">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-[#1a73e8] flex-shrink-0 mt-2"></div>
                      )}
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-[#dadce0] bg-[#f8f9fa]">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="block text-center text-sm text-[#1a73e8] hover:underline"
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
